import { ApiResult } from "./api_result";
import { fetchWithTimeout } from "./fetch_with_timeout";
import { getConfig } from "services/config.service";
import { isNullOrWhitespace } from "../../services/validation.service";

class HttpService {
  timeoutMs: number;

  constructor(timeoutMs?: number) {
    if (timeoutMs) {
      this.timeoutMs = timeoutMs;
    } else {
      const appConfig = getConfig();
      this.timeoutMs = Number(appConfig.httpTimeoutMs);
    }
  }

  private buildPath(path: string): string {
    const backendUrl = getConfig().backendUrl!;
    let url = path;
    if (isNullOrWhitespace(backendUrl)) {
      url = backendUrl + path;
    }
    return url;
  }

  private async safeParseJson<T>(response: Response): Promise<T | undefined> {
    try {
      return await response.json();
    } catch {
      return undefined;
    }
  }

  protected async get<T>(path: string): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });
      const result = new ApiResult<T>().withStatusCode(response.status);
      const data = await this.safeParseJson<T>(response);
      if (data !== undefined) {
        result.withData(data);
      }
      return result;
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<T>().withTimeout();
    }
  }

  protected async getAnonym<ReturnType>(
    path: string,
  ): Promise<ApiResult<ReturnType>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });
      const result = new ApiResult<ReturnType>().withStatusCode(
        response.status,
      );
      const data = await this.safeParseJson<ReturnType>(response);
      if (data !== undefined) {
        result.withData(data);
      }
      return result;
    } catch (error) {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<ReturnType>().withTimeout();
    }
  }

  protected async downloadFile(path: string): Promise<ApiResult<Blob>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "GET",
        timeout: this.timeoutMs,
      });

      if (response.ok) {
        const fileData = await response.blob();
        return new ApiResult<Blob>()
          .withStatusCode(response.status)
          .withData(fileData);
      } else {
        return new ApiResult<Blob>().withStatusCode(response.status);
      }
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<Blob>().withTimeout();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected async post<T>(path: string, content: T): Promise<ApiResult<any>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "POST",
        body: JSON.stringify(content),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = new ApiResult<any>().withStatusCode(response.status);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = await this.safeParseJson<any>(response);
      if (data !== undefined) {
        result.withData(data);
      }
      return result;
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return new ApiResult<any>().withTimeout();
    }
  }

  protected async postForId<T>(
    path: string,
    content: T,
  ): Promise<ApiResult<number>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "POST",
        body: JSON.stringify(content),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });
      const result = new ApiResult<number>().withStatusCode(response.status);
      if (response.ok) {
        const data = await response.text();
        result.withData(parseInt(data));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = await this.safeParseJson<any>(response);
        if (data !== undefined) {
          result.withData(data);
        }
      }
      return result;
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<number>().withTimeout();
    }
  }

  protected async postForData<T>(path: string): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });
      const result = new ApiResult<T>().withStatusCode(response.status);
      const data = await this.safeParseJson<T>(response);
      if (data !== undefined) {
        result.withData(data);
      }
      return result;
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<T>().withTimeout();
    }
  }

  protected async postDataForData<TIn, TOut>(
    path: string,
    content: TIn,
  ): Promise<ApiResult<TOut>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify(content),
        timeout: this.timeoutMs,
      });
      const result = new ApiResult<TOut>().withStatusCode(response.status);
      const data = await this.safeParseJson<TOut>(response);
      if (data !== undefined) {
        result.withData(data);
      }
      return result;
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<TOut>().withTimeout();
    }
  }

  protected async postForm(
    path: string,
    content: FormData,
  ): Promise<ApiResult<void>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "POST",
        body: content,
        timeout: this.timeoutMs,
      });
      return new ApiResult<void>().withStatusCode(response.status);
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<void>().withTimeout();
    }
  }

  protected async uploadFileForm<T>(
    path: string,
    content: FormData,
  ): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "POST",
        body: content,
        timeout: this.timeoutMs,
      });
      if (response.status === 422) {
        return new ApiResult<T>()
          .withStatusCode(response.status)
          .withData(await response.json());
      }
      return new ApiResult<T>().withStatusCode(response.status);
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<T>().withTimeout();
    }
  }

  protected async editFileForm<T>(
    path: string,
    content: FormData,
  ): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "PUT",
        body: content,
        timeout: this.timeoutMs,
      });
      if (response.status === 422) {
        return new ApiResult<T>()
          .withStatusCode(response.status)
          .withData(await response.json());
      }
      return new ApiResult<T>().withStatusCode(response.status);
    } catch {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`);
      return new ApiResult<T>().withTimeout();
    }
  }

  protected async put<T>(path: string, content?: T): Promise<ApiResult<void>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "PUT",
        body: content ? JSON.stringify(content) : null,
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });

      const apiResult = new ApiResult<void>().withStatusCode(response.status);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = await this.safeParseJson<any>(response);
      if (data !== undefined) {
        apiResult.withData(data);
      }

      return apiResult;
    } catch (error) {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`, error);
      return new ApiResult<void>().withTimeout();
    }
  }

  protected async delete<T>(
    path: string,
    content?: T,
  ): Promise<ApiResult<void>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "DELETE",
        body: content ? JSON.stringify(content) : null,
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        timeout: this.timeoutMs,
      });
      return new ApiResult<void>().withStatusCode(response.status);
    } catch (error) {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms`, error);
      return new ApiResult<void>().withTimeout();
    }
  }

  protected async deleteWithResult<T>(path: string): Promise<ApiResult<T>> {
    try {
      const response = await fetchWithTimeout(this.buildPath(path), {
        method: "DELETE",
        timeout: this.timeoutMs,
      });
      return new ApiResult<T>()
        .withStatusCode(response.status)
        .withData(await response.json());
    } catch (error) {
      console.warn(`HTTP timeout after ${this.timeoutMs} ms `, error);
      return new ApiResult<T>().withTimeout();
    }
  }
}

export default HttpService;
